/**
 * Auth Service
 * Centralized service for handling Cognito authentication operations
 */

import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  signOut as cognitoSignOut,
  globalSignOut as cognitoGlobalSignOut,
  confirmForgotPassword as cognitoConfirmForgotPassword,
  confirmSignUp as cognitoConfirmSignUp,
  resendConfirmationCode as cognitoResendCode,
  getIdToken,
  getCurrentUser,
  getCurrentSession,
  refreshSession as cognitoRefreshSession,
  type CognitoUserSession,
} from "../../cognitoConfig";
import * as Sentry from "@sentry/react";
import { isCognitoError, isNetworkError } from "../../utils/error-utils";
import store from "../../store";
import {
  clearSession,
  setLoading,
  setImpersonatedUser,
  updateIdToken,
  updateUser,
} from "../../slices/sessionSlice";
import { stopImpersonation } from "../../slices/impersonationSlice";
import { authApi } from "../../api/authApi";
import { baseApi } from "../../api/api";
import type { User } from "@nicsmith/shared_types";
import i18n from "i18next";

/**
 * Auth Service - handles Cognito authentication
 */
class AuthService {
  private sessionCheckInterval: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  /**
   * Initialize auth state - check for existing session
   * Call this on app startup
   */
  public async initAuthState(): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;

    store.dispatch(setLoading(true));

    try {
      const session = await getCurrentSession();

      if (session && session.isValid()) {
        await this.handleSessionValid(session);
        await this.refreshImpersonatedUser();
        this.startSessionCheck();

        // Invalidate cached course list so hidden courses the user is enrolled
        // in are fetched. Without this, an unauthenticated cache entry from the
        // initial page render (before Cognito validation) persists.
        store.dispatch(baseApi.util.invalidateTags(["Courses"]));
      } else {
        // No valid session
        store.dispatch(clearSession());
      }
    } catch {
      store.dispatch(clearSession());
    } finally {
      store.dispatch(setLoading(false));
    }
  }

  /**
   * Handle valid session - authenticate with backend only when needed.
   *
   * Optimization: if the Cognito token hasn't changed since the last
   * successful authenticate call AND we already have a user with a role
   * in Redux/localStorage, reuse the cached session data. This avoids
   * hitting /auth/authenticate on every page load/refresh, which was
   * causing 200+ unnecessary API calls per session, 429 rate-limit
   * errors, and Google login failures.
   *
   * The server is still called on actual logins (new token) and when
   * the cached session has expired (> 1 hour via rehydrateSession).
   */
  private async handleSessionValid(session: CognitoUserSession): Promise<void> {
    const idToken = session.getIdToken().getJwtToken();
    const state = store.getState().session;

    // Skip the server call if:
    // 1. The token matches what we already have cached
    // 2. The user is already loaded with a role (not a stale/empty session)
    // 3. The session was authenticated recently (lastAuthenticated is set)
    if (
      state.idToken === idToken &&
      state.user?.role &&
      state.isAuthenticated &&
      state.lastAuthenticated
    ) {
      // Token unchanged and user already loaded — use cached data.
      // Avatar and other profile changes on other devices will sync
      // on next actual login (sign-in), not on every page refresh.
      //
      // EXCEPTION: always re-hydrate the subscription/entitlement from the
      // server, even on the cached fast-path. The premium paywall
      // (exercise-page `isPremium`) reads `state.session.user.subscription`,
      // which is only set at login. Without this, a user who bought premium
      // *after* their last login stays locked until a real logout/login,
      // because a plain reload takes this fast-path and never refetches.
      // This is scoped to `subscription` and fires once per app boot (not per
      // render), and /session is not behind the auth rate-limiter, so the
      // anti-rate-limit reason for skipping /auth/authenticate is preserved.
      void this.refreshSubscription(idToken);
      return;
    }

    // Token is new or session is stale — call the server
    try {
      await store
        .dispatch(authApi.endpoints.authenticate.initiate({ idToken, preferred_language: i18n.language }))
        .unwrap();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Re-hydrate the subscription/entitlement portion of the session from the
   * server (GET /api/v1/session returns the full enriched DB user, including
   * the current subscription). Called on the cached fast-path of
   * handleSessionValid so a page reload always reflects the current
   * subscription — e.g. a premium purchase made after the last login — without
   * requiring a logout/login.
   *
   * Deliberately scoped to `subscription`: the heavy /auth/authenticate call is
   * still skipped on reload (the anti-rate-limit optimization stays intact),
   * and this is a single GET per app boot, not per render.
   *
   * Fire-and-forget and never throws: a transient failure just leaves the
   * previous subscription in place until the next boot/login.
   */
  private async refreshSubscription(idToken: string): Promise<void> {
    // Never overwrite the admin's real subscription while impersonating.
    if (store.getState().impersonation?.isImpersonating) return;

    try {
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/v1/session`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!resp.ok) return;

      const freshUser = (await resp.json()) as Partial<NonNullable<User>>;

      // Bail if the session changed underneath us while the request was in
      // flight (logout, token rotation, impersonation started).
      const current = store.getState();
      if (
        current.impersonation?.isImpersonating ||
        !current.session.user ||
        current.session.idToken !== idToken
      ) {
        return;
      }

      // Merge only the subscription so we don't clobber other locally-fresh
      // session fields. updateUser shallow-merges + persists to localStorage,
      // so a subsequent reload keeps the refreshed subscription too.
      store.dispatch(updateUser({ subscription: freshUser.subscription }));
    } catch {
      // Non-fatal: keep the previously known subscription.
    }
  }

  /**
   * When impersonation is active, fetch /session (which returns the target
   * user's data thanks to the X-Impersonate-Uid header) and update the
   * session user in Redux without persisting to localStorage.
   */
  private async refreshImpersonatedUser(): Promise<void> {
    const { impersonation, session } = store.getState();
    if (!impersonation.isImpersonating || !impersonation.targetUid) return;

    const token = session.idToken;
    if (!token) return;

    try {
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/v1/session`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "X-Impersonate-Uid": impersonation.targetUid,
        },
      });
      if (resp.ok) {
        const userData = await resp.json();
        store.dispatch(setImpersonatedUser(userData));
      } else {
        // Target user not found or other error — stop impersonation
        store.dispatch(stopImpersonation());
      }
    } catch {
      store.dispatch(stopImpersonation());
    }
  }

  /**
   * Start periodic session validity check
   */
  private startSessionCheck(): void {
    // Check session every 5 minutes
    this.sessionCheckInterval = setInterval(async () => {
      try {
        const session = await getCurrentSession();
        if (!session || !session.isValid()) {
          // Try to refresh
          await this.refreshToken();
        }
      } catch {
        this.signOut();
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Stop session check
   */
  private stopSessionCheck(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  /**
   * Sign in with email and password
   */
  public async signIn(email: string, password: string): Promise<void> {
    try {
      const session = await cognitoSignIn(email, password);
      await this.handleSessionValid(session);
      this.startSessionCheck();
    } catch (error: unknown) {
      // Handle specific Cognito errors
      if (isCognitoError(error)) {
        if (error.code === "NewPasswordRequired") {
          throw {
            code: "NewPasswordRequired",
            message: i18n.t("auth.errors.newPasswordRequired", "You must set a new password"),
            cognitoUser: error.cognitoUser,
          };
        }
        if (error.code === "UserNotConfirmedException") {
          throw {
            code: "UserNotConfirmed",
            message: i18n.t("auth.errors.pleaseVerifyEmail", "Please verify your email address"),
          };
        }
        if (error.code === "NotAuthorizedException") {
          throw {
            code: "InvalidCredentials",
            message: i18n.t("auth.errors.invalidCredentials", "Invalid email or password"),
          };
        }
      }
      // Suppress network errors (user offline / backend unreachable mid-login).
      // The user still sees the error UI; we just don't get paged for what we
      // can't fix.
      if (!isNetworkError(error)) {
        Sentry.captureException(error, {
          tags: { auth_step: "signin" },
        });
      }
      throw error;
    }
  }

  /**
   * Sign up with email and password
   */
  public async signUp(
    email: string,
    password: string,
    name?: string
  ): Promise<{ userConfirmed: boolean; userSub: string }> {
    try {
      const result = await cognitoSignUp(email, password, { name }, { language: i18n.language });

      return {
        userConfirmed: result.userConfirmed,
        userSub: result.userSub,
      };
    } catch (error: unknown) {
      if (isCognitoError(error)) {
        if (error.code === "UsernameExistsException") {
          throw {
            code: "EmailExists",
            message: i18n.t("auth.errors.emailExists", "An account with this email already exists"),
          };
        }
        if (error.code === "InvalidPasswordException") {
          throw {
            code: "WeakPassword",
            message: i18n.t("auth.errors.weakPassword", "Password does not meet requirements"),
          };
        }
      }
      // Only report unexpected errors to Sentry
      Sentry.captureException(error, {
        tags: { auth_step: "signup" },
        extra: { email, cognito_error_code: isCognitoError(error) ? error.code : undefined },
      });
      throw error;
    }
  }

  /**
   * Confirm sign up with verification code
   */
  public async confirmSignUp(email: string, code: string): Promise<void> {
    try {
      await cognitoConfirmSignUp(email.trim(), code.trim());
    } catch (error: unknown) {
      if (isCognitoError(error)) {
        if (error.code === "CodeMismatchException") {
          throw {
            code: "InvalidCode",
            message: i18n.t("auth.errors.invalidCode", "Invalid verification code"),
          };
        }
        if (error.code === "ExpiredCodeException") {
          throw {
            code: "ExpiredCode",
            message: i18n.t("auth.errors.expiredCode", "Verification code has expired. Please request a new one."),
          };
        }
        if (error.code === "NotAuthorizedException" && error.message?.includes("CONFIRMED")) {
          // User is already confirmed — not an error, just redirect to sign in.
          return;
        }
      }
      Sentry.captureException(error, {
        tags: { auth_step: "confirm_signup" },
        extra: { email, cognito_error_code: isCognitoError(error) ? error.code : undefined },
      });
      throw error;
    }
  }

  /**
   * Resend confirmation code
   */
  public async resendConfirmationCode(email: string): Promise<void> {
    try {
      await cognitoResendCode(email, { language: i18n.language });
    } catch (error: unknown) {
      const cognitoCode = isCognitoError(error) ? error.code : undefined;
      Sentry.captureException(error, {
        tags: { auth_step: "resend_code" },
        extra: { email, cognito_error_code: cognitoCode },
      });
      throw error;
    }
  }

  /**
   * Sign out
   */
  public async signOut(): Promise<void> {
    this.stopSessionCheck();
    store.dispatch(stopImpersonation());
    const state = store.getState();

    try {
      // Notify backend
      if (state.session.idToken) {
        try {
          await store
            .dispatch(
              authApi.endpoints.logout.initiate({
                idToken: state.session.idToken,
              })
            )
            .unwrap();
        } catch {
          // Backend logout error - continue with local logout
        }
      }

      // Sign out from Cognito
      cognitoSignOut();
    } finally {
      store.dispatch(clearSession());
    }
  }

  /**
   * Global sign out (all devices)
   */
  public async globalSignOut(): Promise<void> {
    this.stopSessionCheck();
    const state = store.getState();

    try {
      // Notify backend
      if (state.session.idToken) {
        try {
          await store
            .dispatch(
              authApi.endpoints.logout.initiate({
                idToken: state.session.idToken,
              })
            )
            .unwrap();
        } catch {
          // Backend logout error - continue with local logout
        }
      }

      // Global sign out from Cognito
      await cognitoGlobalSignOut();
    } finally {
      store.dispatch(clearSession());
    }
  }

  /**
   * Initiate password reset
   */
  public async forgotPassword(email: string): Promise<void> {
    const apiUrl = import.meta.env.VITE_API_URL || "";
    const res = await fetch(`${apiUrl}/api/v1/auth/send-password-reset-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({} as Record<string, string>));
      const err = Object.assign(new Error(data.message || "Password reset failed"), {
        code: data.code || "PasswordResetFailed",
      });
      throw err;
    }
  }

  /**
   * Confirm password reset with code
   */
  public async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    await cognitoConfirmForgotPassword(email.trim(), code.trim(), newPassword);
  }

  /**
   * Refresh the ID token
   */
  public async refreshToken(): Promise<string> {
    try {
      const session = await cognitoRefreshSession();
      const freshToken = session.getIdToken().getJwtToken();

      // Update Redux state
      store.dispatch(updateIdToken(freshToken));

      return freshToken;
    } catch (error) {
      store.dispatch(clearSession());
      throw error;
    }
  }

  /**
   * Get current ID token
   */
  public async getIdToken(): Promise<string | null> {
    return getIdToken();
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    const user = getCurrentUser();
    return user !== null;
  }

  /**
   * Handle OAuth session after redirect callback
   * Used for Google Sign-In
   */
  public async handleOAuthSession(session: CognitoUserSession): Promise<void> {
    store.dispatch(setLoading(true));

    try {
      await this.handleSessionValid(session);
      this.startSessionCheck();
    } finally {
      store.dispatch(setLoading(false));
    }
  }

  /**
   * Cleanup on unmount
   */
  public cleanup(): void {
    this.stopSessionCheck();
    this.isInitialized = false;
  }
}

export const authService = new AuthService();
export default authService;
